import React, { Suspense, useEffect } from 'react';
import {
  HashRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import Nav from '../Nav/Nav';
import Footer from '../Footer/Footer';

import ProtectedRoute from '../ProtectedRoute/ProtectedRoute';
import FantasyOnly from '../common/FantasyOnly';

import LandingPage from '../LandingPage/LandingPage';
import AppThemeProvider from '../../theme/AppThemeProvider';
import OfflineBanner from '../OfflineBanner/OfflineBanner';
import { SnackbarProvider } from '../Snackbar/SnackbarProvider';
import NavigationGuard from '../NavigationGuard/NavigationGuard';
import PushAlertToast from '../PushAlertToast/PushAlertToast';
import { MIN_TOUCH_TARGET_SX } from '../../shared/lib/a11y';

// LoginForm.css and RegisterForm.css load in their lazy chunks, after App.css, and all three
// define global .formPanel/.alert: import them first so App.css wins, as before the split.
import '../LoginForm/LoginForm.css';
import '../RegisterForm/RegisterForm.css';
import './App.css';
import { lazyWithReload } from '../../shared/lib/lazyWithReload';
import { hasSessionHint } from '../../lib/sessionHint';

// The landing page is the only one an anonymous first visit needs; every other
// route's code loads when it is opened (Register and Login once the landing is idle,
// UserPage at boot for a returning user, so those hops never show the spinner).
const AboutPage = lazyWithReload(() => import('../AboutPage/AboutPage'));
const UserPage = lazyWithReload(() => import('../UserPage/UserPage'));
const InfoPage = lazyWithReload(() => import('../InfoPage/InfoPage'));
const LoginPage = lazyWithReload(() => import('../LoginPage/LoginPage'));
const RegisterPage = lazyWithReload(() => import('../RegisterPage/RegisterPage'));
const ForgotPassword = lazyWithReload(() => import('../ForgotPassword/ForgotPassword'));
const ResetPassword = lazyWithReload(() => import('../ResetPassword/ResetPassword'));
const VerifyEmail = lazyWithReload(() => import('../VerifyEmail/VerifyEmail'));
const NotFound = lazyWithReload(() => import('../NotFound/NotFound'));
const LeagueManagement = lazyWithReload(() => import('../LeagueManagement/LeagueManagement'));
const LeagueDiscovery = lazyWithReload(() => import('../LeagueDiscovery/LeagueDiscovery'));
const LineupPage = lazyWithReload(() => import('../../pages/lineup'));
const PlayerManagement = lazyWithReload(() => import('../PlayerManagement/PlayerManagement'));
const LeagueDashboardPage = lazyWithReload(() => import('../../pages/league-dashboard'));
const MatchupPage = lazyWithReload(() => import('../../pages/matchup'));
const GameCenterPage = lazyWithReload(() => import('../../pages/game-center'));
const CommissionerConsolePage = lazyWithReload(() => import('../../pages/commissioner-console'));
const DraftBoard = lazyWithReload(() => import('../DraftBoard/DraftBoard'));
const DraftSettings = lazyWithReload(() => import('../DraftSettings/DraftSettings'));
const LeagueRules = lazyWithReload(() => import('../LeagueRules/LeagueRules'));
const PickemPage = lazyWithReload(() => import('../../pages/pickem'));
const DraftPresenter = lazyWithReload(() => import('../DraftPresenter/DraftPresenter'));
const WaiversPage = lazyWithReload(() => import('../../pages/waivers'));
const TradeCenter = lazyWithReload(() => import('../TradeCenter/TradeCenter'));
const TransactionLog = lazyWithReload(() => import('../TransactionLog/TransactionLog'));
const PowerRankings = lazyWithReload(() => import('../PowerRankings/PowerRankings'));
const LeagueHistoryPage = lazyWithReload(() => import('../../pages/league-history'));
const LeagueTrophiesPage = lazyWithReload(() => import('../../pages/league-trophies'));
const NotificationPrefs = lazyWithReload(() => import('../NotificationPrefs/NotificationPrefs'));
const AuthenticatedPlayerProfilePage = lazyWithReload(() => import('../PlayerDetail/AuthenticatedPlayerProfilePage'));
const AdminDashboard = lazyWithReload(() => import('../AdminDashboard/AdminDashboard'));
const DraftSimScreen = lazyWithReload(() => import('../DraftSim/DraftSimScreen'));

if (hasSessionHint()) UserPage.preload().catch(() => {});

// Mounted with the anonymous landing page: after it has painted and the browser is
// idle, fetch the two pages its CTAs and the header link to.
function PreloadAuthPages() {
  useEffect(() => {
    const run = () => [RegisterPage, LoginPage].forEach((page) => page.preload().catch(() => {}));
    if (typeof window.requestIdleCallback !== 'function') {
      const timer = setTimeout(run, 1000);
      return () => clearTimeout(timer);
    }
    const handle = window.requestIdleCallback(run, { timeout: 3000 });
    return () => window.cancelIdleCallback(handle);
  }, []);
  return null;
}

// The Draft route path this session's skip link targets (issue #121). Kept
// as a narrow, route-scoped check rather than a site-wide skip link: the
// #draft-main-content region it points at only exists on this one route
// today (see DraftBoard.jsx). Broader coverage is parent-spec #108 territory.
// Trailing slash is optional to match how react-router itself matches this
// route (matchPath ignores a trailing "/" when `end` is true, the default
// for a route with no children) - without the `\/?`, a stray trailing slash
// would mount DraftBoard while this regex silently failed to match it.
const DRAFT_ROUTE_PATTERN = /^\/league\/[^/]+\/draft\/?$/;

// Routes whose page renders a region with a known id (inside the shell's one
// <main>), so the skip link has a real target. Still route-scoped (a site-wide skip link is parent-spec
// #108): /user joined in the Home v2 quick fixes.
const SKIP_LINK_TARGETS = [
  { pattern: DRAFT_ROUTE_PATTERN, targetId: 'draft-main-content' },
  { pattern: /^\/user\/?$/, targetId: 'user-main-content' },
];

// HashRouter reads the URL's #fragment as the app's OWN route, so a plain
// `href="#draft-main-content"` skip link would make the browser's native
// same-page anchor navigation look like a route change to `/draft-main-
// content` instead of scrolling/focusing the target. Handle the click
// ourselves - focus the target directly - and never let that hash reach
// the router.
function skipToMainContent(event, targetId) {
  event.preventDefault();
  const target = document.getElementById(targetId);
  if (!target) return;
  target.focus();
  target.scrollIntoView();
}

function AppLayout({ children }) {
  const { pathname } = useLocation();
  const muiTheme = useTheme();
  const isPresenter = pathname.startsWith('/present/');
  const isDraftRoute = DRAFT_ROUTE_PATTERN.test(pathname);
  const skipTargetId = SKIP_LINK_TARGETS.find(({ pattern }) => pattern.test(pathname))?.targetId;
  // Desktop's Draft room gets its own viewport-height shell (issue #122):
  // a fixed-height flex column so DraftBoard's two internal scroll regions
  // are the only things that ever scroll there - the page itself doesn't.
  // Below the medium breakpoint the Draft route keeps the site's ordinary
  // single-scroll-region page, Footer included. Always called (never behind
  // `isDraftRoute &&`) so the hook order never varies between renders.
  const isDesktopViewport = useMediaQuery(muiTheme.breakpoints.up('md'));
  const isDesktopDraftShell = isDraftRoute && isDesktopViewport;

  const outerSx = isPresenter
    ? { minHeight: '100vh' }
    : isDesktopDraftShell
      ? { display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }
      : { display: 'flex', flexDirection: 'column', minHeight: '100vh' };
  const contentSx = isPresenter
    ? undefined
    : isDesktopDraftShell
      ? { flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
      : { flexGrow: 1, display: 'flex', flexDirection: 'column' };

  return (
    <Box sx={outerSx}>
      {skipTargetId && (
        <Link
          href={`#${skipTargetId}`}
          onClick={(event) => skipToMainContent(event, skipTargetId)}
          sx={{
            position: 'fixed',
            top: 8,
            left: 8,
            zIndex: (theme) => theme.zIndex.tooltip + 1,
            transform: 'translateY(-200%)',
            transition: 'transform 0.15s ease-in',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            bgcolor: 'background.paper',
            color: 'text.primary',
            px: 2,
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 1,
            border: '2px solid',
            borderColor: 'primary.main',
            fontWeight: 600,
            textDecoration: 'none',
            '&:focus': { transform: 'translateY(0)' },
            ...MIN_TOUCH_TARGET_SX,
          }}
        >
          Skip to main content
        </Link>
      )}
      {!isPresenter && <Nav />}
      <Box component="main" sx={contentSx}>
        {children}
      </Box>
      {!isPresenter && !isDesktopDraftShell && <Footer />}
    </Box>
  );
}

const pageLoading = (
  <Box role="status" aria-label="Loading page" sx={{ py: 8, textAlign: 'center' }}>
    <CircularProgress />
  </Box>
);

function LegacyLineupRedirect() {
  const { leagueId } = useParams();
  const [searchParams] = useSearchParams();
  // Everything the caller put in the query rides along. The Bench what-if card
  // links here with the swap it found (`?swapOut=&swapIn=`, #910) and Team is
  // where that swap is read; rebuilding the query from `leagueId` alone dropped
  // it on the doorstep.
  const params = new URLSearchParams(searchParams);
  params.set('leagueId', leagueId);
  return <Navigate to={`/team?${params.toString()}`} replace />;
}

function App() {
  const dispatch = useDispatch();

  const user = useSelector(store => store.user);
  const sessionResolved = useSelector(store => store.session.resolved);

  useEffect(() => {
    dispatch({ type: 'FETCH_USER' });
  }, [dispatch]);

  return (
    <AppThemeProvider>
    <SnackbarProvider>
    <OfflineBanner />
    <Router>
      <PushAlertToast />
      <NavigationGuard>
      <AppLayout>
        <Suspense fallback={pageLoading}>
        <Routes>
          <Route path="/present/:token" element={<DraftPresenter />} />
          <Route path="/league" element={<ProtectedRoute><LeagueManagement /></ProtectedRoute>} />
          <Route path="/league/join" element={<ProtectedRoute><LeagueManagement /></ProtectedRoute>} />
          <Route path="/discover" element={<ProtectedRoute><LeagueDiscovery /></ProtectedRoute>} />
          <Route path="/team" element={<ProtectedRoute><LineupPage /></ProtectedRoute>} />
          <Route path="/player" element={<ProtectedRoute><PlayerManagement /></ProtectedRoute>} />
          <Route path="/league/:leagueId" element={<ProtectedRoute><LeagueDashboardPage /></ProtectedRoute>} />
          <Route path="/league/:leagueId/matchups/:matchupId" element={<ProtectedRoute><FantasyOnly><MatchupPage /></FantasyOnly></ProtectedRoute>} />
          <Route path="/league/:leagueId/game-center" element={<ProtectedRoute><FantasyOnly><GameCenterPage /></FantasyOnly></ProtectedRoute>} />
          <Route path="/league/:leagueId/draft" element={<ProtectedRoute><FantasyOnly mainContentId="draft-main-content"><DraftBoard /></FantasyOnly></ProtectedRoute>} />
          <Route path="/league/:leagueId/draft-settings" element={<ProtectedRoute><FantasyOnly><DraftSettings /></FantasyOnly></ProtectedRoute>} />
          <Route path="/league/:leagueId/commissioner" element={<ProtectedRoute><CommissionerConsolePage /></ProtectedRoute>} />
          <Route path="/league/:leagueId/rules" element={<ProtectedRoute><LeagueRules /></ProtectedRoute>} />
          <Route path="/league/:leagueId/pickem" element={<ProtectedRoute><PickemPage /></ProtectedRoute>} />
          <Route path="/league/:leagueId/lineup" element={<ProtectedRoute><FantasyOnly><LegacyLineupRedirect /></FantasyOnly></ProtectedRoute>} />
          <Route path="/league/:leagueId/waivers" element={<ProtectedRoute><FantasyOnly><WaiversPage /></FantasyOnly></ProtectedRoute>} />
          <Route path="/league/:leagueId/trades" element={<ProtectedRoute><FantasyOnly><TradeCenter /></FantasyOnly></ProtectedRoute>} />
          <Route path="/league/:leagueId/activity" element={<ProtectedRoute><TransactionLog /></ProtectedRoute>} />
          <Route path="/league/:leagueId/power-rankings" element={<ProtectedRoute><FantasyOnly><PowerRankings /></FantasyOnly></ProtectedRoute>} />
          <Route path="/league/:leagueId/history" element={<ProtectedRoute><LeagueHistoryPage /></ProtectedRoute>} />
          <Route path="/league/:leagueId/trophies" element={<ProtectedRoute><LeagueTrophiesPage /></ProtectedRoute>} />
          <Route path="/draft-sim" element={<ProtectedRoute><DraftSimScreen /></ProtectedRoute>} />
          <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPrefs /></ProtectedRoute>} />
          <Route path="/players/:playerId" element={<ProtectedRoute><AuthenticatedPlayerProfilePage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          {/* Visiting localhost:3000 will redirect to localhost:3000/home */}
          <Route path="/" element={<Navigate to="/home" replace />} />

          {/* Visiting localhost:3000/about will show the about page. */}
          <Route
            // shows AboutPage at all times (logged in or not)
            path="/about"
            element={<AboutPage />}
          />

          {/* For protected routes, the view could show one of several things on the same route.
            Visiting localhost:3000/user will show the UserPage if the user is logged in.
            If the user is not logged in, the ProtectedRoute will show the LoginPage (component).
            Even though it seems like they are different pages, the user is always on localhost:3000/user */}
          <Route
            // logged in shows UserPage else shows LoginPage
            path="/user"
            element={<ProtectedRoute><UserPage /></ProtectedRoute>}
          />

          <Route
            // logged in shows InfoPage else shows LoginPage
            path="/info"
            element={<ProtectedRoute><InfoPage /></ProtectedRoute>}
          />

          <Route
            path="/login"
            element={
              user.id ?
                // If the user is already logged in,
                // redirect to the /user page
                <Navigate to="/user" replace />
                :
                // Otherwise, show the login page
                <LoginPage />
            }
          />

          <Route
            path="/registration"
            element={
              user.id ?
                // If the user is already logged in,
                // redirect them to the /user page
                <Navigate to="/user" replace />
                :
                // Otherwise, show the registration page
                <RegisterPage />
            }
          />

          <Route
            path="/home"
            element={
              user.id ?
                // If the user is already logged in,
                // redirect them to the /user page
                <Navigate to="/user" replace />
                : !sessionResolved && hasSessionHint() ?
                // A hard load by a logged-in user is `user: {}` until SET_USER
                // lands; the landing page would mount, fetch its public data,
                // and be redirected away (#2097). The hint says someone was
                // logged in here, so wait; a cold visitor paints at once.
                pageLoading
                :
                // Otherwise, show the Landing page
                <><LandingPage /><PreloadAuthPages /></>
            }
          />

          {/* If none of the other routes matched, we will show a 404. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </AppLayout>
      </NavigationGuard>
    </Router>
    </SnackbarProvider>
    </AppThemeProvider>
  );
}

export default App;
